import { useCallback } from 'react'
import { Control, SubmitHandler, useForm } from 'react-hook-form'
import AccountSection from '../AccountSection/AccountSection'
import Button from '../Button/Button'
import { CheckboxInput, PhoneInput } from '../Form'
import Icon from '../Icon/Icon'
import Link from '../Link/Link'

export interface SmsInfo {
  smsTrackingOptin: boolean
  smsTrackingPhoneNumber?: string
  smsTrackingTermsAccepted: boolean
}

export interface AccountSmsSignupProps {
  /**
   * Callback function to handle form submission.
   */
  onSubmit: SubmitHandler<SmsInfo>
  /**
   * User's current SMS information
   */
  smsInfo?: SmsInfo
}

export default function AccountSmsSignup({
  onSubmit,
  smsInfo,
}: Readonly<AccountSmsSignupProps>) {
  const { control, handleSubmit } = useForm<SmsInfo>()

  const submitForm = useCallback(
    (data: SmsInfo) => {
      onSubmit({
        ...data,
        smsTrackingOptin: true,
        smsTrackingPhoneNumber: `+${data.smsTrackingPhoneNumber?.replace(/\D/g, '')}`,
      })
    },
    [onSubmit],
  )

  return (
    <AccountSection title="Sign up for SMS text notifications for your orders.">
      <form
        onSubmit={handleSubmit(submitForm)}
        className="flex flex-col gap-y-4 md:gap-y-6"
      >
        <PhoneInput
          name="smsTrackingPhoneNumber"
          control={control as unknown as Control}
          label="Phone number"
          placeholder="555-555-5555"
          rules={{
            required: {
              message: 'Phone number is requred',
              value: true,
            },
          }}
          defaultValue={smsInfo?.smsTrackingPhoneNumber?.replace(
            /^\+?1?(\d{3})(\d{3})(\d{4})$/,
            '+1 $1-$2-$3',
          )}
          disabled={!!smsInfo?.smsTrackingOptin}
        />
        <Button
          type="submit"
          className="md:w-[278px]"
          disabled={!!smsInfo?.smsTrackingOptin}
        >
          Sign up
        </Button>
        {!smsInfo?.smsTrackingOptin && (
          <div>
            <CheckboxInput
              label={
                <span className="type-body-8">
                  I agree to the SMS Program{' '}
                  <Link
                    href="/terms"
                    inline
                    className="!text-xs md:!text-sm !font-normal !underline-offset-1"
                  >
                    Terms of Use
                  </Link>{' '}
                  and{' '}
                  <Link
                    href="/privacy"
                    inline
                    className="!text-xs md:!text-sm !font-normal !underline-offset-1"
                  >
                    Privacy Policy
                  </Link>{' '}
                  and certify this is my phone number
                </span>
              }
              control={control as unknown as Control}
              name="smsTrackingTermsAccepted"
              className="!items-start"
              rules={{
                required: {
                  message: 'Agreement is required',
                  value: true,
                },
              }}
            />
          </div>
        )}
        {!!smsInfo?.smsTrackingOptin && (
          <div className="flex items-center gap-x-2">
            <Icon
              icon="check-circle-filled"
              className="w-[18px] h-[18px] text-success"
            />
            <span className="type-body-16">Signed up successfully</span>
          </div>
        )}
      </form>
    </AccountSection>
  )
}
